/**
 * Turns a picked spreadsheet or text file into what the workshop uploads. The
 * backend never parses a file: it stores plain UTF-8 text and the agent reads it
 * a page at a time. So the browser does the converting:
 *
 * - An Excel workbook (xlsx, xls) becomes one CSV per non-empty sheet.
 * - A UTF-16 text file (Excel's "Unicode text" export) is re-encoded as UTF-8.
 * - Anything else is uploaded as picked.
 */

/** The same cap as the backend's, on the picked file and on every CSV made from it. */
export const MAX_DATA_FILE_BYTES = 5 * 1024 * 1024;
const MAX_MB = MAX_DATA_FILE_BYTES / (1024 * 1024);

const WORKBOOK_EXTENSIONS = [".xlsx", ".xls"];

export function isWorkbookFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return WORKBOOK_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function baseName(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx > 0 ? fileName.slice(0, idx) : fileName;
}

const UNREADABLE_WORKBOOK =
  "Couldn't read this spreadsheet. If it is password-protected, remove the password and try again.";

async function workbookToCsvFiles(file: File): Promise<File[]> {
  const bytes = await file.arrayBuffer();
  // An xlsx is a zip ("PK"), or an OLE container ("\xD0\xCF") when encrypted.
  // Anything else SheetJS would quietly read as plain text and turn into a
  // garbage CSV. (An xls can legitimately be HTML or XML, so it is not checked.)
  const head = new Uint8Array(bytes, 0, Math.min(2, bytes.byteLength));
  const zipOrOle =
    (head[0] === 0x50 && head[1] === 0x4b) || (head[0] === 0xd0 && head[1] === 0xcf);
  if (file.name.toLowerCase().endsWith(".xlsx") && !zipOrOle) {
    throw new Error(UNREADABLE_WORKBOOK);
  }

  // Loaded on demand: it is a large library, and only a workbook upload needs it.
  const XLSX = await import("xlsx");
  let workbook: import("xlsx").WorkBook;
  try {
    workbook = XLSX.read(bytes, {
      type: "array",
      dense: true,
      cellFormula: false,
      cellHTML: false,
      // Excel's default date format follows the viewer's locale, so its
      // "m/d/yy" rendering is ambiguous (1/5/24). ISO is not.
      dateNF: "yyyy-mm-dd",
    });
  } catch {
    throw new Error(UNREADABLE_WORKBOOK);
  }

  const sheets = workbook.SheetNames.map((name) => ({
    name,
    csv: XLSX.utils.sheet_to_csv(workbook.Sheets[name], { blankrows: false, strip: true }),
  })).filter((sheet) => sheet.csv.trim().length > 0);
  if (sheets.length === 0) throw new Error("This spreadsheet has no data in it.");

  const base = baseName(file.name);
  return sheets.map((sheet) => {
    const name = sheets.length === 1 ? `${base}.csv` : `${base} - ${sheet.name}.csv`;
    const csv = new File([sheet.csv], name, { type: "text/csv" });
    if (csv.size > MAX_DATA_FILE_BYTES) {
      throw new Error(
        sheets.length === 1
          ? `This spreadsheet is over ${MAX_MB} MB once converted to CSV.`
          : `Sheet "${sheet.name}" is over ${MAX_MB} MB once converted to CSV.`,
      );
    }
    return csv;
  });
}

/** UTF-16 (by its byte-order mark) re-encoded as UTF-8; anything else untouched. */
async function asUtf8(file: File): Promise<File> {
  const head = new Uint8Array(await file.slice(0, 2).arrayBuffer());
  const encoding =
    head[0] === 0xff && head[1] === 0xfe
      ? "utf-16le"
      : head[0] === 0xfe && head[1] === 0xff
        ? "utf-16be"
        : null;
  if (!encoding) return file;
  const text = new TextDecoder(encoding).decode(await file.arrayBuffer());
  return new File([text], file.name, { type: file.type });
}

/**
 * The file(s) to upload for one picked spreadsheet or text file. Throws with a
 * message fit to show the user.
 */
export async function prepareDataFile(file: File): Promise<File[]> {
  if (file.size > MAX_DATA_FILE_BYTES) {
    throw new Error(`File is too large (max ${MAX_MB} MB)`);
  }
  if (isWorkbookFile(file)) return workbookToCsvFiles(file);
  return [await asUtf8(file)];
}
